import { useEffect, useState } from "react";
import api, { inr, formatApiError } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import Invoice from "@/components/Invoice";
import { Printer, Eye, XCircle, CreditCard, Pause, Play, MessageCircle, CheckCheck } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const STATUSES = ["all", "pending_approval", "open", "billed", "paid", "cancelled"];
const MODES = ["cash", "card", "upi", "bank", "credit"];

export default function Orders() {
  const nav = useNavigate();
  const [orders, setOrders] = useState([]);
  const [status, setStatus] = useState("all");
  const [view, setView] = useState(null);
  const [showParkedOnly, setShowParkedOnly] = useState(false);

  const [payOrder, setPayOrder] = useState(null);
  const [pays, setPays] = useState([{ mode: "cash", amount: 0, reference: "" }]);
  const [payTip, setPayTip] = useState(0);
  const [busy, setBusy] = useState(false);

  const load = async (s) => {
    const params = s === "all" ? {} : { status_filter: s };
    const { data } = await api.get("/orders", { params });
    setOrders(data);
  };
  useEffect(() => { load(status); }, [status]);

  const cancel = async (o) => {
    if (!window.confirm(`Cancel ${o.order_no}?`)) return;
    try { await api.post(`/orders/${o.id}/cancel`); toast.success("Cancelled"); load(status); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const togglePark = async (o) => {
    try {
      if (o.parked) {
        await api.post(`/orders/${o.id}/unpark`);
        toast.success(`${o.order_no} resumed`);
      } else {
        await api.post(`/orders/${o.id}/park`);
        toast.success(`${o.order_no} parked`);
      }
      load(status);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const approve = async (o) => {
    try {
      await api.post(`/orders/${o.id}/approve`);
      toast.success(`${o.order_no} approved → KOT sent`);
      load(status);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const startPay = (o) => {
    const existingTip = Number(o.tip || 0);
    const remaining = Math.max(0, (o.total || 0) - (o.paid_amount || 0));
    setPayOrder(o);
    setPayTip(existingTip);
    setPays([{ mode: "cash", amount: remaining, reference: "" }]);
  };

  const submitPay = async () => {
    if (!payOrder) return;
    const sum = pays.reduce((a, b) => a + Number(b.amount || 0), 0);
    let workingOrder = payOrder;

    // If tip changed in Bill & Pay, update the order first so total/due stays consistent server-side.
    if (Number(payTip || 0) !== Number(payOrder.tip || 0)) {
      const updatePayload = {
        type: payOrder.type,
        table_id: payOrder.table_id || null,
        customer_id: payOrder.customer_id || null,
        customer_name: payOrder.customer_name || null,
        customer_phone: payOrder.customer_phone || null,
        outlet_id: payOrder.outlet_id || null,
        items: (payOrder.items || []).map((it) => ({
          menu_item_id: it.menu_item_id,
          name: it.name,
          price: Number(it.price || 0),
          qty: Number(it.qty || 1),
          gst_rate: Number(it.gst_rate || 5),
          modifiers: it.modifiers || [],
          notes: it.notes || "",
        })),
        discount: Number(payOrder.discount || 0),
        discount_type: payOrder.discount_type || "flat",
        tip: Number(payTip || 0),
        notes: payOrder.notes || "",
      };

      const { data: updated } = await api.put(`/orders/${payOrder.id}`, updatePayload);
      workingOrder = updated;
      setPayOrder(updated);
    }

    const remaining = Math.max(0, (workingOrder.total || 0) - (workingOrder.paid_amount || 0));
    if (sum + 0.01 < remaining) {
      toast.error(`Short by ${inr(remaining - sum)}`);
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/orders/${workingOrder.id}/pay`, pays.filter((p) => Number(p.amount) > 0));
      toast.success("Payment recorded");
      setPayOrder(null);
      setPayTip(0);
      setView(data);
      load(status);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const sendWhatsApp = (o) => {
    if (!o.customer_phone) {
      toast.error("Customer phone is missing on this order");
      return;
    }
    const phone = (o.customer_phone || "").replace(/[^\d]/g, "");
    const msg = `Hi ${o.customer_name || ""}, your bill: ${o.order_no} for ${inr(o.total)}. Thank you for dining with us!`;
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  const statusBadge = (s, parked) => {
    const map = {
      pending_approval: "bg-[#FFE9D7] text-[#D95D39] border border-[#D95D39]",
      open: "bg-[#FBE9E2] text-[#D95D39]",
      billed: "bg-[#FFF4E0] text-[#E9A23B]",
      paid: "bg-[#E5EBE3] text-[#6A7F66]",
      cancelled: "bg-[#F4F2EE] text-[#7A7873]",
    };
    return (
      <div className="flex flex-col gap-0.5 items-start">
        <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${map[s] || ""}`}>{s.replace("_", " ")}</span>
        {parked && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#FFF4E0] text-[#E9A23B] uppercase tracking-wider">Parked</span>}
      </div>
    );
  };

  const filtered = showParkedOnly ? orders.filter((o) => o.parked) : orders;

  return (
    <div className="p-6 sm:p-8" data-testid="orders-page">
      <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading font-extrabold text-3xl text-[#2D2C2A]">Orders</h1>
          <p className="text-sm text-[#7A7873] mt-1">Bill, park, approve self-orders, send WhatsApp receipts & reprint invoices.</p>
        </div>
        <Button
          variant={showParkedOnly ? "default" : "outline"}
          className={showParkedOnly ? "bg-[#E9A23B] hover:bg-[#D6912E]" : "border-[#EBE8E2]"}
          onClick={() => setShowParkedOnly((v) => !v)}
          data-testid="parked-only-toggle"
        >
          <Pause size={14} className="mr-1.5" /> {showParkedOnly ? "Showing Parked" : "Show Parked"}
        </Button>
      </div>

      <Tabs value={status} onValueChange={setStatus} className="mb-4">
        <TabsList className="bg-white border border-[#EBE8E2] flex-wrap h-auto">
          {STATUSES.map((s) => (
            <TabsTrigger key={s} value={s} className="data-[state=active]:bg-[#FBE9E2] data-[state=active]:text-[#D95D39] capitalize" data-testid={`status-${s}`}>
              {s.replace("_", " ")}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="bg-white border border-[#EBE8E2] rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[920px]">
          <thead className="bg-[#F4F2EE] text-[#7A7873] text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-2.5">Order / Token</th>
              <th className="text-left px-4 py-2.5">Time</th>
              <th className="text-left px-4 py-2.5">Type</th>
              <th className="text-left px-4 py-2.5">Customer / Table</th>
              <th className="text-right px-4 py-2.5">Items</th>
              <th className="text-right px-4 py-2.5">Total</th>
              <th className="text-left px-4 py-2.5">Status</th>
              <th className="px-4 py-2.5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((o) => (
              <tr key={o.id} className="border-t border-[#EBE8E2]" data-testid={`order-row-${o.id}`}>
                <td className="px-4 py-2.5 font-mono-num font-semibold">
                  {o.order_no}
                  {o.token_no && <div className="text-[10px] text-[#D95D39]">{o.token_no}</div>}
                </td>
                <td className="px-4 py-2.5 text-[#7A7873] text-xs">{new Date(o.created_at).toLocaleString("en-IN")}</td>
                <td className="px-4 py-2.5 capitalize">{o.type}</td>
                <td className="px-4 py-2.5">{o.customer_name || (o.table_id ? "Table" : "—")}</td>
                <td className="px-4 py-2.5 text-right font-mono-num">{(o.items || []).length}</td>
                <td className="px-4 py-2.5 text-right font-mono-num font-semibold">{inr(o.total)}</td>
                <td className="px-4 py-2.5">{statusBadge(o.status, o.parked)}</td>
                <td className="px-4 py-2.5 text-right whitespace-nowrap">
                  {o.status === "pending_approval" && (
                    <Button size="sm" className="h-7 mr-1 bg-[#6A7F66] hover:bg-[#566653]" onClick={() => approve(o)} data-testid={`approve-${o.id}`}>
                      <CheckCheck size={13} className="mr-1" /> Approve
                    </Button>
                  )}
                  {(o.status === "open" || o.status === "billed") && (
                    <>
                      <Button size="sm" className="h-7 mr-1 bg-[#D95D39] hover:bg-[#C24F2C]" onClick={() => startPay(o)} data-testid={`bill-pay-${o.id}`}>
                        <CreditCard size={13} className="mr-1" /> Bill & Pay
                      </Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7" title="Add more items" onClick={() => nav(`/pos?order=${o.id}`)} data-testid={`resume-${o.id}`}>
                        <Play size={13} />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7" title={o.parked ? "Resume from parked" : "Park bill"} onClick={() => togglePark(o)} data-testid={`park-${o.id}`}>
                        <Pause size={13} className={o.parked ? "text-[#6A7F66]" : ""} />
                      </Button>
                    </>
                  )}
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setView(o)} data-testid={`view-${o.id}`}>
                    <Eye size={13} />
                  </Button>
                  {o.customer_phone && (
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-[#25D366]" title="Send WhatsApp" onClick={() => sendWhatsApp(o)} data-testid={`wa-${o.id}`}>
                      <MessageCircle size={13} />
                    </Button>
                  )}
                  {o.status !== "paid" && o.status !== "cancelled" && (
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-[#E76F51]" onClick={() => cancel(o)}>
                      <XCircle size={13} />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-10 text-center text-[#7A7873]">No orders.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Invoice / View dialog */}
      <Dialog open={!!view} onOpenChange={(v) => !v && setView(null)}>
        <DialogContent className="invoice-print-dialog bg-white max-w-md p-0 max-h-[90vh] overflow-y-auto">
          {view && <Invoice order={view} />}
          <div className="flex gap-2 p-4 border-t border-[#EBE8E2] no-print">
            <Button variant="outline" className="flex-1" onClick={() => setView(null)}>Close</Button>
            <Button className="flex-1 bg-[#D95D39] hover:bg-[#C24F2C]" onClick={() => window.print()}>
              <Printer size={16} className="mr-1.5" /> Print
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bill & Pay dialog */}
      <Dialog
        open={!!payOrder}
        onOpenChange={(v) => {
          if (!v) {
            setPayOrder(null);
            setPayTip(0);
          }
        }}
      >
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle>Bill &amp; Pay · {payOrder?.order_no}</DialogTitle>
          </DialogHeader>
          {payOrder && (
            <div className="space-y-3">
              <div className="grid grid-cols-[120px_1fr] gap-2 items-end">
                <div className="text-[10px] text-[#7A7873]">Tip (₹)</div>
                <Input
                  type="number"
                  value={payTip}
                  onChange={(e) => setPayTip(e.target.value)}
                  data-testid="bill-pay-tip"
                />
              </div>
              <div className="text-sm flex justify-between font-mono-num bg-[#FBFAF8] p-3 rounded-md">
                <span>Total Due</span>
                <span className="font-bold">{inr(Math.max(0, ((payOrder.total || 0) - (Number(payOrder.tip || 0)) + Number(payTip || 0)) - (payOrder.paid_amount || 0)))}</span>
              </div>
              {pays.map((p, i) => (
                <div key={i} className="grid grid-cols-[110px_1fr_1fr] gap-2 items-end">
                  <div>
                    <div className="text-[10px] text-[#7A7873] mb-0.5">Mode</div>
                    <Select value={p.mode} onValueChange={(v) => setPays((arr) => { const c = [...arr]; c[i] = { ...c[i], mode: v }; return c; })}>
                      <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {MODES.map((m) => <SelectItem key={m} value={m} className="capitalize">{m}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <div className="text-[10px] text-[#7A7873] mb-0.5">Amount</div>
                    <Input type="number" value={p.amount} onChange={(e) => setPays((arr) => { const c = [...arr]; c[i] = { ...c[i], amount: e.target.value }; return c; })} data-testid={`pay-amount-${i}`} />
                  </div>
                  <div>
                    <div className="text-[10px] text-[#7A7873] mb-0.5">Ref (UPI/Card)</div>
                    <Input value={p.reference} onChange={(e) => setPays((arr) => { const c = [...arr]; c[i] = { ...c[i], reference: e.target.value }; return c; })} />
                  </div>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={() => setPays((arr) => [...arr, { mode: "upi", amount: 0, reference: "" }])}>
                + Split payment
              </Button>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayOrder(null)}>Cancel</Button>
            <Button className="bg-[#D95D39] hover:bg-[#C24F2C]" onClick={submitPay} disabled={busy} data-testid="confirm-pay">
              {busy ? "Processing…" : "Confirm Payment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
